import Vue from 'vue'
import util from '@/js/Util'
import {
  SENSOR_DATA_TEMPERATURE,
  SENSOR_DATA_POWER,
  SENSOR_DATA_IS_ON,
  DEVICE_STATUS_SWITCHED_OFF,
  DEVICE_STATUS_SWITCHED_ON,
  DEVICE_STATUS_NOT_REACHABLE,
  DEVICE_MODE_CHANGED_TO_MEASURE,
} from '@/constants'

function preprocessDevice(rawDevice) {
  const monday = rawDevice['manual_time_slots_mon_']
  const tuesday = rawDevice['manual_time_slots_tue_']
  const wednesday = rawDevice['manual_time_slots_wed_']
  const thursday = rawDevice['manual_time_slots_thu_']
  const friday = rawDevice['manual_time_slots_fri_']
  const saturday = rawDevice['manual_time_slots_sat_']
  const sunday = rawDevice['manual_time_slots_sun_']

  const setForAllDays =
    monday === tuesday &&
    tuesday === wednesday &&
    wednesday === thursday &&
    thursday === friday &&
    friday === saturday &&
    saturday === sunday &&
    sunday === monday

  const active = rawDevice.mode === 'manual'
  return {
    ...rawDevice,
    timer: {
      // Whether or not a timer value has been changed by the user.
      changed: false,
      active: active,
      isValid: true,
      setForAllDays: setForAllDays,
      defaultOnState: rawDevice.default_state === 'true',

      all: util.parseTimeSlots(monday),
      monday: util.parseTimeSlots(monday),
      tuesday: util.parseTimeSlots(tuesday),
      wednesday: util.parseTimeSlots(wednesday),
      thursday: util.parseTimeSlots(thursday),
      friday: util.parseTimeSlots(friday),
      saturday: util.parseTimeSlots(saturday),
      sunday: util.parseTimeSlots(sunday),
      original: {
        active: active,
        setForAllDays: setForAllDays,
        all: util.parseTimeSlots(monday),
        monday: util.parseTimeSlots(monday),
        tuesday: util.parseTimeSlots(tuesday),
        wednesday: util.parseTimeSlots(wednesday),
        thursday: util.parseTimeSlots(thursday),
        friday: util.parseTimeSlots(friday),
        saturday: util.parseTimeSlots(saturday),
        sunday: util.parseTimeSlots(sunday),
      },
    },
    isReachable: 'unknown',
    // This will be filled with the reads data with data from a separate request.
    reads: {},
    consumption: [],
  }
}

function devicesReceived(state, rawDevices) {
  const devices = rawDevices.map(preprocessDevice)
  Vue.set(state.cube, 'devices', devices)
  Vue.set(state.cube, 'devicesLoaded', true)
}

function newDevicesReceived(state, newDevices) {
  const devices = newDevices.map(preprocessDevice)
  Vue.set(state.cube, 'devices', state.cube.devices.concat(devices))
}

// When we request a full device state from the backend.
function deviceStateReceived(state, data) {
  const deviceIndex = util.getDeviceIndex(state, data.deviceId)
  Vue.set(state.cube.devices, deviceIndex, {
    reads: {},
    ...state.cube.devices[deviceIndex],
    isOn: !!data.data.data.switchIsOn,
    // See comment in toggleDeviceOn action for why we need this.
    isOnChangedLocally: false,
    wattage: data.data.data.power,
    temperature: data.data.data.temperature,
    isReachable: true,
  })
}

function deviceNotReachable(state, deviceId) {
  const deviceIndex = util.getDeviceIndex(state, deviceId)
  Vue.set(state.cube.devices[deviceIndex], 'reads', {})
  Vue.set(state.cube.devices[deviceIndex], 'consumption', [])
  Vue.set(state.cube.devices[deviceIndex], 'isReachable', false)
}

function setDeviceIsOn(state, data) {
  const deviceIndex = util.getDeviceIndex(state, data.deviceId)
  Vue.set(state.cube.devices[deviceIndex], 'isOn', data.isOn)
}

function setDeviceIsOnChangedLocally(state, data) {
  const deviceIndex = util.getDeviceIndex(state, data.deviceId)
  Vue.set(state.cube.devices[deviceIndex], 'isOnChangedLocally', data.value)
}

function setDeviceMode(state, data) {
  const deviceIndex = util.getDeviceIndex(state, data.deviceId)
  Vue.set(state.cube.devices[deviceIndex], 'mode', data.mode)
}

function deviceSelectTimerSlot(state, data) {
  const deviceIndex = util.getDeviceIndex(state, data.deviceId)
  Vue.set(state.cube.devices[deviceIndex].timer, 'selectedIndex', data.index)
}

function deviceData(state, data) {
  const deviceIndex = util.getDeviceIndex(state, data.deviceId)
  if (data.name) {
    state.cube.devices[deviceIndex].name = data.name
  }
  if (data.description) {
    state.cube.devices[deviceIndex].description = data.description
  }
  if (data.appliance_type) {
    state.cube.devices[deviceIndex].appliance_type = data.appliance_type
  }
  if (data.deviceType) {
    state.cube.devices[deviceIndex].deviceType = data.deviceType
  }

  Vue.set(state.cube.devices, deviceIndex, state.cube.devices[deviceIndex])
}

function deviceReads(state, data) {
  const deviceIndex = util.getDeviceIndex(state, data.deviceId)
  let reads = {
    labels: data.result.labels,
    datasets: data.result.reads.map((d) => {
      const consumption = d.reading_types['Consumption W']
      return {
        data:
          data.timespan === 'day'
            ? consumption.values
            : consumption.calculated_wh.values,
        sum: consumption.calculated_wh.sum,
        label: d.device_name,
        type: d.appliance_type.toLowerCase(),
      }
    }),
  }
  Vue.set(state.cube.devices[deviceIndex], 'reads', reads)

  const sums = data.result.reads
    .map((d) => {
      return {
        value: d.reading_types['Consumption W'].calculated_wh.sum,
        start: new Date(d.original_start_time),
        end: d.original_end_time ? new Date(d.original_end_time) : false,
        label: d.device_name,
        type: d.appliance_type.toLowerCase(),
      }
    })
    .sort((a, b) => b.start - a.start)
  Vue.set(state.cube.devices[deviceIndex], 'consumption', sums)
}

function addDeviceTimeSlot(state, data) {
  const deviceIndex = util.getDeviceIndex(state, data.deviceId)

  // Add the slot to all days (including 'all) if data.day is all, or just the
  // given day if not.
  let days = [data.day]
  if (data.day === 'all') {
    days.push(
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday'
    )
  }

  days.map((day) => {
    const slots = state.cube.devices[deviceIndex].timer[day]
    const lastSlot = slots.length > 0 ? slots[slots.length - 1] : false
    if (!lastSlot || util.canAddDeviceTimeSlot(lastSlot)) {
      slots.push(util.getNextDeviceTimeSlot(lastSlot))
      Vue.set(state.cube.devices[deviceIndex].timer, 'changed', true)
    }
  })
}

function updateDeviceTimeSlot(state, data) {
  const deviceIndex = util.getDeviceIndex(state, data.deviceId)

  // Update all days (including 'all') if data.day is all, or just the given day
  // if not.
  let days = [data.day]
  if (data.day === 'all') {
    days.push(
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday'
    )
  }

  days.map((day) => {
    const slot = state.cube.devices[deviceIndex].timer[day][data.slotIndex]
    if (slot.start !== data.start || slot.end !== data.end) {
      Vue.set(state.cube.devices[deviceIndex].timer, 'changed', true)
      // Setting the 'start' and 'end' properties on slot with Vue.set did not
      // trigger reactivity as I would have expected.
      Vue.set(state.cube.devices[deviceIndex].timer[day], data.slotIndex, {
        ...slot,
        start: data.start,
        end: data.end,
      })
    }
  })

  let day = data.day
  if (data.day === 'all') {
    day = 'monday'
  }
  const valid = util.validateTimeSlots(
    state.cube.devices[deviceIndex].timer[day]
  )
  Vue.set(state.cube.devices[deviceIndex].timer, 'isValid', valid)
}

function removeDeviceTimeSlot(state, data) {
  const deviceIndex = util.getDeviceIndex(state, data.deviceId)

  // Update all days (including 'all') if data.day is all, or just the given day
  // if not.
  let days = [data.day]
  if (data.day === 'all') {
    days.push(
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday'
    )
  }

  days.map((day) => {
    state.cube.devices[deviceIndex].timer[day].splice(data.slotIndex, 1)
    Vue.set(state.cube.devices[deviceIndex].timer, 'changed', true)
  })
}

function deviceRestoreOriginalTimerValues(state, deviceId) {
  const deviceIndex = util.getDeviceIndex(state, deviceId)
  const original = state.cube.devices[deviceIndex].timer.original
  Vue.set(state.cube.devices[deviceIndex].timer, 'active', original.active)
  Vue.set(
    state.cube.devices[deviceIndex].timer,
    'setForAllDays',
    original.setForAllDays
  )
  Vue.set(state.cube.devices[deviceIndex].timer, 'all', original.all)
  Vue.set(
    state.cube.devices[deviceIndex].timer,
    'defaultOnState',
    original.defaultOnState
  )

  const days = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ]
  days.map((day) => {
    Vue.set(
      state.cube.devices[deviceIndex].timer,
      day,
      util.cloneTimeSlots(original[day])
    )
  })
  Vue.set(state.cube.devices[deviceIndex].timer, 'changed', false)
}

function deviceTimerSaved(state, deviceId) {
  const deviceIndex = util.getDeviceIndex(state, deviceId)
  Vue.set(state.cube.devices[deviceIndex].timer, 'changed', false)
}

function showDeleteDevicePopup(state, value = true) {
  Vue.set(state.cube, 'showDeleteDevicePopup', !!value)
}

function deviceDeleted(state, deviceId) {
  const deviceIndex = util.getDeviceIndex(state, deviceId)
  state.cube.devices.splice(deviceIndex, 1)
}

function setDeviceTimerActive(state, data) {
  const deviceIndex = util.getDeviceIndex(state, data.deviceId)
  Vue.set(state.cube.devices[deviceIndex].timer, 'active', data.active)
  Vue.set(state.cube.devices[deviceIndex].timer, 'changed', true)
}

function setDeviceTimerAllDaysActive(state, data) {
  const deviceIndex = util.getDeviceIndex(state, data.deviceId)
  Vue.set(state.cube.devices[deviceIndex].timer, 'setForAllDays', data.active)
  Vue.set(state.cube.devices[deviceIndex].timer, 'changed', true)
}

function deviceSensorDataReceived(state, data) {
  let deviceIndex = -1
  try {
    // Note, that as of 2021-07-12 the app receives sensor data events for
    // devices it does not know about due to a but in the logic for scanning for
    // new devices.
    // To prevent visible errors we handle this gracefully here.
    deviceIndex = util.getDeviceIndex(state, data.deviceId)
  } catch (e) {
    return
  }
  data.data.map((row) => {
    switch (row.dataType) {
      case SENSOR_DATA_TEMPERATURE:
        Vue.set(state.cube.devices[deviceIndex], 'temperature', row.value)
        break

      case SENSOR_DATA_POWER:
        Vue.set(state.cube.devices[deviceIndex], 'wattage', row.value)
        break

      case SENSOR_DATA_IS_ON:
        Vue.set(state.cube.devices[deviceIndex], 'isOn', row.value)
        setDeviceIsOnChangedLocally(state, {
          deviceId: data.deviceId,
          value: false,
        })
        break
    }
  })
  // If the device came back online automatically mark it that way.
  Vue.set(state.cube.devices[deviceIndex], 'isReachable', true)
}

function deviceStatusChangeReceived(state, data) {
  const deviceIndex = util.getDeviceIndex(state, data.deviceId)

  switch (parseInt(data.statusChangeId)) {
    case DEVICE_STATUS_SWITCHED_ON:
      Vue.set(state.cube.devices[deviceIndex], 'isOn', true)
      Vue.set(state.cube.devices[deviceIndex], 'isReachable', true)
      setDeviceIsOnChangedLocally(state, {
        deviceId: data.deviceId,
        value: false,
      })
      break

    case DEVICE_STATUS_SWITCHED_OFF:
      Vue.set(state.cube.devices[deviceIndex], 'isOn', false)
      Vue.set(state.cube.devices[deviceIndex], 'wattage', 0)
      Vue.set(state.cube.devices[deviceIndex], 'isReachable', true)
      setDeviceIsOnChangedLocally(state, {
        deviceId: data.deviceId,
        value: false,
      })
      break

    case DEVICE_STATUS_NOT_REACHABLE:
      Vue.set(state.cube.devices[deviceIndex], 'isReachable', false)
      break

    case DEVICE_MODE_CHANGED_TO_MEASURE:
      Vue.set(state.cube.devices[deviceIndex], 'mode', 'measure')
      Vue.set(state.cube.devices[deviceIndex].timer, 'active', false)
      break
  }
}

function deviceTimerSortSlots(state, deviceId) {
  const deviceIndex = util.getDeviceIndex(state, deviceId)
  const compareStart = (a, b) => {
    // Basic sorting start time by converting the str to an int.
    // E.g.: "12:39" -> 1239 and comparing these values.
    const startA = parseInt(a.start.replace(/[^0-9]+/, ''))
    const startB = parseInt(b.start.replace(/[^0-9]+/, ''))
    return startA - startB
  }

  const all = state.cube.devices[deviceIndex].timer.all
  const monday = state.cube.devices[deviceIndex].timer.monday
  const tuesday = state.cube.devices[deviceIndex].timer.tuesday
  const wednesday = state.cube.devices[deviceIndex].timer.wednesday
  const thursday = state.cube.devices[deviceIndex].timer.thursday
  const friday = state.cube.devices[deviceIndex].timer.friday
  const saturday = state.cube.devices[deviceIndex].timer.saturday
  const sunday = state.cube.devices[deviceIndex].timer.sunday

  all.sort(compareStart)
  monday.sort(compareStart)
  tuesday.sort(compareStart)
  wednesday.sort(compareStart)
  thursday.sort(compareStart)
  friday.sort(compareStart)
  saturday.sort(compareStart)
  sunday.sort(compareStart)

  Vue.set(state.cube.devices[deviceIndex].timer, 'all', all)
  Vue.set(state.cube.devices[deviceIndex].timer, 'monday', monday)
  Vue.set(state.cube.devices[deviceIndex].timer, 'tuesday', tuesday)
  Vue.set(state.cube.devices[deviceIndex].timer, 'wednesday', wednesday)
  Vue.set(state.cube.devices[deviceIndex].timer, 'thursday', thursday)
  Vue.set(state.cube.devices[deviceIndex].timer, 'friday', friday)
  Vue.set(state.cube.devices[deviceIndex].timer, 'saturday', saturday)
  Vue.set(state.cube.devices[deviceIndex].timer, 'sunday', sunday)
}

function setDeviceDefaultOnState(state, data) {
  const deviceIndex = util.getDeviceIndex(state, data.deviceId)
  Vue.set(
    state.cube.devices[deviceIndex].timer,
    'defaultOnState',
    data.defaultOnState
  )
  Vue.set(state.cube.devices[deviceIndex].timer, 'changed', true)
}

export default {
  devicesReceived,
  newDevicesReceived,
  deviceStateReceived,
  deviceNotReachable,
  setDeviceIsOn,
  setDeviceIsOnChangedLocally,
  setDeviceMode,
  deviceSelectTimerSlot,
  deviceData,
  deviceReads,
  updateDeviceTimeSlot,
  addDeviceTimeSlot,
  deviceRestoreOriginalTimerValues,
  removeDeviceTimeSlot,
  deviceTimerSaved,
  showDeleteDevicePopup,
  deviceDeleted,
  setDeviceTimerActive,
  setDeviceTimerAllDaysActive,
  deviceSensorDataReceived,
  deviceStatusChangeReceived,
  deviceTimerSortSlots,
  setDeviceDefaultOnState,
}
